import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { PDFViewer } from "@react-pdf/renderer";
import axios from "axios";

import DetailDialog from "../../components/DetailDialog";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";
import ContractPDF from "../../utils/ContractPDF";
import { endPoint, endPointServer } from "../../global";
import { useAuth } from "../../context/AuthProvider";

const poppins = {
  fontFamily: "'Poppins', sans-serif",
};

const chartColors = [
  "#60a5fa",
  "#22d3ee",
  "#a78bfa",
  "#34d399",
  "#fbbf24",
  "#f87171",
];

const pageWrapper = {
  width: "100%",
  zIndex: 1,
  position: "relative",
  px: { xs: 1.5, md: 2.2 },
  py: { xs: 1.5, md: 2 },
};

const glassPanel = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px",
  background:
    "linear-gradient(145deg, rgba(8,22,51,0.78), rgba(15,45,92,0.48))",
  border: "1px solid rgba(147,197,253,0.22)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: "0 16px 42px rgba(0,0,0,0.3)",
};

const topPanel = {
  ...glassPanel,
  p: { xs: 1.5, md: 1.8 },
  "&::before": {
    content: '""',
    position: "absolute",
    width: 145,
    height: 145,
    borderRadius: "50%",
    top: -75,
    right: -55,
    background: "rgba(96,165,250,0.16)",
    filter: "blur(4px)",
  },
};

const tablePanel = {
  ...glassPanel,
  p: { xs: 1.2, md: 1.5 },
};

const iconBox = {
  width: 38,
  height: 38,
  borderRadius: "13px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(96,165,250,0.16)",
  border: "1px solid rgba(147,197,253,0.28)",
  color: "#93c5fd",
  flexShrink: 0,
};

const actionBtn = (color) => ({
  ...poppins,
  fontWeight: 700,
  fontSize: "0.68rem",
  textTransform: "none",
  borderRadius: "9px",
  px: 1.15,
  py: 0.42,
  minWidth: 0,
  boxShadow: "none",

  ...(color === "blue" && {
    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #1e40af, #1d4ed8)",
      boxShadow: "0 6px 16px rgba(37,99,235,0.28)",
    },
  }),

  ...(color === "red" && {
    backgroundColor: "rgba(239,68,68,0.14)",
    color: "#fca5a5",
    border: "1px solid rgba(248,113,113,0.28)",
    "&:hover": {
      backgroundColor: "rgba(239,68,68,0.24)",
    },
  }),
});

const confirmButtonSx = {
  ...poppins,
  fontWeight: 900,
  fontSize: "0.68rem",
  textTransform: "none",
  borderRadius: "999px",
  px: 1.25,
  py: 0.48,
  minWidth: 0,
  color: "#fff",
  background: "linear-gradient(135deg, #2563eb, #38bdf8)",
  boxShadow: "0 6px 16px rgba(37,99,235,0.3)",
  border: "1px solid rgba(147,197,253,0.42)",
  "&:hover": {
    background: "linear-gradient(135deg, #1d4ed8, #0ea5e9)",
    boxShadow: "0 8px 22px rgba(37,99,235,0.42)",
    transform: "translateY(-1px)",
  },
};

const confirmedBadgeSx = {
  ...poppins,
  height: 26,
  px: 0.65,
  borderRadius: "999px",
  color: "#6ee7b7",
  fontWeight: 900,
  fontSize: "0.68rem",
  backgroundColor: "rgba(16,185,129,0.12)",
  border: "1px solid rgba(52,211,153,0.35)",
  "& .MuiChip-icon": {
    color: "#6ee7b7",
    fontSize: 15,
  },
};

const gridSx = {
  border: "1px solid rgba(147,197,253,0.18)",
  backgroundColor: "rgba(3,10,26,0.42)",
  color: "#fff",
  borderRadius: "16px",
  overflow: "hidden",
  minWidth: 1080,

  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "rgba(15,35,78,0.95)",
    borderBottom: "1px solid rgba(147,197,253,0.16)",
    minHeight: "42px !important",
    maxHeight: "42px !important",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "rgba(15,35,78,0.95)",
    minHeight: "42px !important",
    maxHeight: "42px !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff",
    fontWeight: 800,
    fontSize: "0.7rem",
    fontFamily: "'Poppins', sans-serif",
  },

  "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
    color: "rgba(219,234,254,0.72)",
  },

  "& .MuiDataGrid-row": {
    borderBottom: "1px solid rgba(147,197,253,0.08)",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.74rem",
    color: "rgba(255,255,255,0.86)",
    backgroundColor: "rgba(255,255,255,0.02)",
    minHeight: "46px !important",
    maxHeight: "46px !important",
    "&:hover": {
      backgroundColor: "rgba(96,165,250,0.08)",
    },
  },

  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    color: "rgba(255,255,255,0.86)",
    minHeight: "46px !important",
    maxHeight: "46px !important",
    py: "4px",
  },

  "& .MuiDataGrid-footerContainer": {
    minHeight: "42px",
    backgroundColor: "rgba(15,35,78,0.82)",
    borderTop: "1px solid rgba(147,197,253,0.16)",
    color: "rgba(219,234,254,0.82)",
  },

  "& .MuiTablePagination-root": {
    color: "rgba(219,234,254,0.82)",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.74rem",
  },

  "& .MuiTablePagination-selectIcon": {
    color: "#93c5fd",
  },

  "& .MuiIconButton-root": {
    color: "#93c5fd",
    p: 0.6,
  },

  "& .MuiDataGrid-filler": {
    backgroundColor: "rgba(15,35,78,0.95)",
  },

  "& .MuiDataGrid-scrollbarFiller": {
    backgroundColor: "rgba(15,35,78,0.95)",
  },
};

const searchInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.78rem",
    borderRadius: "13px",

    "& fieldset": {
      borderColor: "rgba(147,197,253,0.18)",
    },

    "&:hover fieldset": {
      borderColor: "rgba(147,197,253,0.42)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
    },
  },

  "& .MuiInputBase-input": {
    py: 1,
  },

  "& input::placeholder": {
    color: "rgba(219,234,254,0.55)",
    opacity: 1,
  },
};

const getPackageColor = (packageName) => {
  const name = String(packageName || "").toLowerCase();

  if (name.includes("bronze")) return "#f59e0b";
  if (name.includes("silver")) return "#cbd5e1";
  if (name.includes("gold")) return "#fbbf24";
  if (name.includes("platinum")) return "#a78bfa";

  return "#60a5fa";
};

const InfoLine = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    }}
  >
    <Box sx={{ color: "#93c5fd", display: "flex" }}>{icon}</Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          ...poppins,
          color: "rgba(219,234,254,0.55)",
          fontSize: "0.62rem",
          lineHeight: 1,
          mb: 0.28,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          ...poppins,
          color: "#fff",
          fontSize: "0.76rem",
          fontWeight: 700,
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const SalesMobileCard = ({ row, onCoupon, onConfirm, onDelete, onViewPdf }) => (
  <Box
    sx={{
      ...glassPanel,
      p: 1.45,
      borderRadius: "18px",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontWeight: 900,
            fontSize: "0.92rem",
            lineHeight: 1.15,
          }}
        >
          {row.user}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.62)",
            fontSize: "0.72rem",
            mt: 0.3,
          }}
        >
          {row.storeName}
        </Typography>
      </Box>

      <Chip
        icon={row.konfirm === 1 ? <CheckCircleIcon /> : undefined}
        label={row.konfirm === 1 ? "Terkonfirmasi" : "Belum Konfirmasi"}
        size="small"
        sx={{
          ...poppins,
          flexShrink: 0,
          height: 24,
          fontWeight: 900,
          fontSize: "0.62rem",
          color: row.konfirm === 1 ? "#6ee7b7" : "#fbbf24",
          backgroundColor:
            row.konfirm === 1
              ? "rgba(16,185,129,0.12)"
              : "rgba(245,158,11,0.14)",
          border:
            row.konfirm === 1
              ? "1px solid rgba(52,211,153,0.35)"
              : "1px solid rgba(251,191,36,0.35)",
          "& .MuiChip-icon": {
            color: row.konfirm === 1 ? "#6ee7b7" : "#fbbf24",
            fontSize: 14,
          },
        }}
      />
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        mt: 1.4,
      }}
    >
      <InfoLine
        icon={<LocationOnOutlinedIcon fontSize="small" />}
        label="Area"
        value={row.area}
      />

      <InfoLine
        icon={<LocalPhoneOutlinedIcon fontSize="small" />}
        label="Phone"
        value={row.phoneNo}
      />

      <InfoLine
        icon={<TableRestaurantOutlinedIcon fontSize="small" />}
        label="Table"
        value={row.tableNo}
      />

      <InfoLine
        icon={<StorefrontOutlinedIcon fontSize="small" />}
        label="Toko"
        value={row.storeName}
      />
    </Box>

    <Box
      sx={{
        mt: 1.4,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
      }}
    >
      <Box
        sx={{
          px: 1.1,
          py: 0.9,
          borderRadius: "12px",
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(147,197,253,0.16)",
        }}
      >
        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.58)",
            fontSize: "0.62rem",
            mb: 0.3,
          }}
        >
          Paket
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: getPackageColor(row.packageName),
            fontSize: "0.82rem",
            fontWeight: 900,
            lineHeight: 1.15,
          }}
        >
          {row.packageName || "-"}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.1,
          py: 0.9,
          borderRadius: "12px",
          background: "rgba(96,165,250,0.14)",
          border: "1px solid rgba(147,197,253,0.22)",
        }}
      >
        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.58)",
            fontSize: "0.62rem",
            mb: 0.3,
          }}
        >
          Quantity
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontSize: "0.9rem",
            fontWeight: 900,
            lineHeight: 1.15,
          }}
        >
          {row.totalQuantity} Paket
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 0.8,
        mt: 1.4,
      }}
    >
      <Button
        onClick={() => onCoupon(row)}
        startIcon={<ConfirmationNumberOutlinedIcon />}
        sx={actionBtn("blue")}
      >
        Kupon
      </Button>

      <Button
        onClick={() => onViewPdf(row)}
        startIcon={<VisibilityOutlinedIcon />}
        sx={actionBtn("blue")}
      >
        PDF
      </Button>

      {row.konfirm === 0 && (
        <Button
          onClick={() => onConfirm(row)}
          startIcon={<CheckCircleOutlineOutlinedIcon />}
          sx={confirmButtonSx}
        >
          Konfirmasi
        </Button>
      )}

      <Button
        onClick={() => onDelete(row)}
        startIcon={<DeleteOutlineOutlinedIcon />}
        sx={actionBtn("red")}
      >
        Hapus
      </Button>
    </Box>
  </Box>
);

export default function Sales() {
  const { token } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [contractData, setContractData] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  const [dataSales, setDataSales] = useState([]);
  const [search, setSearch] = useState("");

  const [dataUsers, setDataUsers] = useState([]);
  const [searchUserBelumBeli, setSearchUserBelumBeli] = useState("");

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${endPoint}getUserNotPurchased`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDataUsers(res.data ?? []);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg ?? "Gagal mengambil data user.");
      setSuccess(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${endPoint}getSales`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDataSales(res.data ?? []);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg ?? "Terjadi kesalahan.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  const rows = useMemo(
    () =>
      dataSales.map((item) => {
        const totalCoupon =
          Number(item.totalCoupon) || Number(item.totalQuantity) || 0;

        const hasCouponRange =
          item.couponStart && item.couponEnd && totalCoupon > 0;

        const couponText = hasCouponRange
          ? `${item.couponStart} - ${item.couponEnd} (${totalCoupon} kupon)`
          : item.couponText || item.DaftarKupon || "";

        return {
          id: item.salesId,
          userId: item.userId,
          user: item.name,
          storeName: item.storeName,
          area: item.area,
          phoneNo: item.phoneNo,
          address: item.address,
          packageName: item.packageName,
          totalQuantity: Number(item.totalQuantity) || 0,
          konfirm: Number(item.konfirm) || 0,

          daftarKupon: couponText,
          couponText,
          couponStart: item.couponStart,
          couponEnd: item.couponEnd,
          totalCoupon,

          salesId: item.salesId,
          pdfPath: item.pdfPath,
          tableNo: item.tableNo,
        };
      }),
    [dataSales],
  );

  const confirmedRows = useMemo(
    () => rows.filter((row) => row.konfirm === 1),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    if (!keyword) return rows;

    return rows.filter((row) =>
      [
        row.user,
        row.storeName,
        row.area,
        row.phoneNo,
        row.address,
        row.tableNo,
        row.packageName,
        row.totalQuantity,
        row.couponText,
        row.konfirm === 1 ? "terkonfirmasi" : "belum konfirmasi",
      ]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [rows, search]);

  const totalSales = rows.length;

  const totalConfirmed = rows.filter((row) => row.konfirm === 1).length;

  const totalCoupons = rows.reduce((total, row) => {
    return total + Number(row.totalCoupon || 0);
  }, 0);

  const topPackages = useMemo(() => {
    const map = {};

    confirmedRows.forEach((row) => {
      const packageName = row.packageName || "Tidak Ada Paket";

      if (!map[packageName]) {
        map[packageName] = {
          name: packageName,
          count: 0,
          totalQuantity: 0,
        };
      }

      map[packageName].count += 1;
      map[packageName].totalQuantity += Number(row.totalQuantity) || 0;
    });

    return Object.values(map).sort((a, b) => b.totalQuantity - a.totalQuantity);
  }, [confirmedRows]);

  const topUsersByPurchase = useMemo(() => {
    return [...confirmedRows]
      .filter((row) => row.totalQuantity > 0)
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .slice(0, 5);
  }, [confirmedRows]);

  const handleOpenCoupon = (row) => {
    setSelectedTransaction({
      ...row,
      DaftarKupon: row.couponText || row.daftarKupon || "",
      daftarKupon: row.couponText || row.daftarKupon || "",
    });

    setIsDialogOpen(true);
  };

  const handleOpenViewDialog = async (row) => {
    setOpenViewDialog(true);
    setContractData(null);
    setLoading(true);

    try {
      const res = await axios.get(
        `${endPoint}getDataContract?salesId=${row.salesId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setContractData(res.data);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg || "Gagal mengambil data kontrak.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const confirmSales = async (row) => {
    setLoading(true);

    try {
      const res = await axios.put(
        `${endPoint}confirmSales`,
        {
          salesId: row.salesId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await fetchData();

      setOpenAlert(true);
      setResponse(res.data.msg);
      setSuccess(true);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg || "Gagal konfirmasi.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const deleteSales = async (row) => {
    if (!window.confirm("Yakin ingin menghapus transaksi ini?")) return;

    setLoading(true);

    try {
      const res = await axios.put(
        `${endPoint}deleteSales`,
        {
          salesId: row.salesId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await fetchData();

      setOpenAlert(true);
      setResponse(res.data.msg);
      setSuccess(true);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg || "Gagal menghapus data.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToWhatsApp = async () => {
    setLoading(true);

    try {
      const fileUrl = contractData?.pathPdf;

      if (!fileUrl) {
        throw new Error("Gagal mendapatkan file PDF");
      }

      const cleanFileUrl = fileUrl.replace(/\\/g, "/");
      const fullFileUrl = `${endPointServer}${
        cleanFileUrl.startsWith("/") ? "" : "/"
      }${cleanFileUrl}`;

      const formDataWA = new FormData();

      formDataWA.append("no_whatsapp", contractData.info?.phoneNo || "");
      formDataWA.append("nama", contractData.info?.name || "");
      formDataWA.append("nama_toko", contractData.info?.storeName || "");
      formDataWA.append("url_dokumen", fullFileUrl);

      await axios.post(
        "https://alderon.zeinvitation.com/api/send-message",
        formDataWA,
      );

      setOpenAlert(true);
      setResponse("Berhasil dikirim ke WhatsApp!");
      setSuccess(true);
    } catch (error) {
      setOpenAlert(true);
      setResponse(
        error.response?.data?.msg || error.message || "Gagal kirim ke WhatsApp",
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const cleanedData = rows.map((item) => ({
      userId: item.userId,
      namaUser: item.user,
      namaToko: item.storeName,
      area: item.area,
      phoneNo: item.phoneNo,
      address: item.address,
      tableNo: item.tableNo,
      paket: item.packageName,
      quantity: item.totalQuantity,
      konfirm: item.konfirm,
      daftarKupon: item.couponText || item.daftarKupon,
      salesId: item.salesId,
      pdfPath: item.pdfPath
        ? `${endPoint}${item.pdfPath.replace(/\\/g, "/")}`
        : "",
    }));

    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "DataUser");

    const buf = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buf], {
        type: "application/octet-stream",
      }),
      "Data-User.xlsx",
    );
  };

  const renderTopPackageCard = (pkg) => (
    <Box
      key={pkg.name}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        background: "rgba(255,255,255,0.07)",
        border: "1px solid rgba(147,197,253,0.16)",
        minHeight: 112,
      }}
    >
      <Box
        sx={{
          p: 1.15,
          background: "rgba(15,35,78,0.58)",
          borderBottom: "1px solid rgba(147,197,253,0.14)",
        }}
      >
        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontWeight: 900,
            fontSize: "0.78rem",
          }}
        >
          {pkg.name}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: getPackageColor(pkg.name),
            fontWeight: 900,
            fontSize: "1.08rem",
            mt: 0.4,
            lineHeight: 1,
          }}
        >
          {pkg.totalQuantity}
        </Typography>
      </Box>

      <Box sx={{ p: 1.1 }}>
        <Typography
          sx={{
            ...poppins,
            color: "rgba(255,255,255,0.86)",
            fontSize: "0.7rem",
            fontWeight: 700,
          }}
        >
          {pkg.count} transaksi
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.55)",
            fontSize: "0.65rem",
            mt: 0.25,
          }}
        >
          Total quantity paket
        </Typography>
      </Box>
    </Box>
  );

  const renderTopUserCard = (item, index) => (
    <Box
      key={item.userId}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "30px 1fr",
          sm: "30px 1fr auto",
        },
        gap: 1,
        alignItems: "center",
        p: 1.05,
        borderRadius: "13px",
        background: "rgba(255,255,255,0.07)",
        border: "1px solid rgba(147,197,253,0.16)",
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "10px",
          background: "rgba(96,165,250,0.16)",
          border: "1px solid rgba(147,197,253,0.24)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: chartColors[index % chartColors.length],
          fontWeight: 900,
          fontSize: "0.76rem",
          ...poppins,
        }}
      >
        {index + 1}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontWeight: 800,
            fontSize: "0.78rem",
            lineHeight: 1.15,
          }}
        >
          {item.user}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.62)",
            fontSize: "0.66rem",
            mt: 0.22,
          }}
        >
          {item.storeName} • {item.area}
        </Typography>
      </Box>

      <Box
        sx={{
          gridColumn: { xs: "1 / -1", sm: "auto" },
          justifySelf: { xs: "start", sm: "end" },
          px: 1.1,
          py: 0.55,
          borderRadius: "999px",
          background: "rgba(96,165,250,0.14)",
          border: "1px solid rgba(147,197,253,0.22)",
        }}
      >
        <Typography
          sx={{
            ...poppins,
            color: "#fff",
            fontSize: "0.7rem",
            fontWeight: 900,
          }}
        >
          {item.totalQuantity} Paket
        </Typography>
      </Box>
    </Box>
  );

  const columns = [
    {
      field: "user",
      headerName: "Nama User",
      minWidth: 145,
      flex: 1,
    },
    {
      field: "storeName",
      headerName: "Nama Toko",
      minWidth: 145,
      flex: 1,
    },
    {
      field: "area",
      headerName: "Area",
      minWidth: 115,
      flex: 0.75,
    },
    {
      field: "phoneNo",
      headerName: "Nomor HP",
      minWidth: 125,
      flex: 0.8,
    },
    {
      field: "tableNo",
      headerName: "Table",
      minWidth: 80,
      flex: 0.45,
    },
    {
      field: "packageName",
      headerName: "Paket",
      minWidth: 145,
      flex: 0.85,
      renderCell: (params) => (
        <Chip
          label={params.row.packageName || "-"}
          size="small"
          sx={{
            ...poppins,
            height: 24,
            fontSize: "0.66rem",
            fontWeight: 900,
            color: getPackageColor(params.row.packageName),
            backgroundColor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(147,197,253,0.18)",
          }}
        />
      ),
    },
    {
      field: "totalQuantity",
      headerName: "Qty",
      minWidth: 75,
      flex: 0.45,
    },
    {
      field: "detail",
      headerName: "Kupon",
      minWidth: 95,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => handleOpenCoupon(params.row)}
          sx={actionBtn("blue")}
        >
          Kupon
        </Button>
      ),
    },
    {
      field: "konfirm",
      headerName: "Status",
      minWidth: 155,
      sortable: false,
      renderCell: (params) =>
        params.row.konfirm === 0 ? (
          <Button
            variant="contained"
            onClick={() => confirmSales(params.row)}
            startIcon={<CheckCircleOutlineOutlinedIcon />}
            sx={confirmButtonSx}
          >
            Konfirmasi
          </Button>
        ) : (
          <Chip
            icon={<CheckCircleIcon />}
            label="Terkonfirmasi"
            size="small"
            sx={confirmedBadgeSx}
          />
        ),
    },
    {
      field: "delete",
      headerName: "Hapus",
      minWidth: 85,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => deleteSales(params.row)}
          sx={actionBtn("red")}
        >
          Hapus
        </Button>
      ),
    },
    {
      field: "view",
      headerName: "PDF",
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => handleOpenViewDialog(params.row)}
          sx={actionBtn("blue")}
        >
          Lihat
        </Button>
      ),
    },
  ];

  const usersBelumPembelianRows = useMemo(() => {
    const userYangSudahBeli = new Set(rows.map((row) => String(row.userId)));

    return dataUsers
      .filter((user) => !userYangSudahBeli.has(String(user.userId)))
      .map((user) => ({
        id: user.userId,
        userId: user.userId,
        user: user.name,
        storeName: user.storeName,
        area: user.area,
        phoneNo: user.phoneNo,
        address: user.address,
        tableNo: user.tableNo,
      }));
  }, [dataUsers, rows]);

  const filteredUsersBelumPembelian = useMemo(() => {
    const keyword = searchUserBelumBeli.toLowerCase().trim();

    if (!keyword) return usersBelumPembelianRows;

    return usersBelumPembelianRows.filter((row) =>
      [row.user, row.storeName, row.area, row.phoneNo, row.address, row.tableNo]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [usersBelumPembelianRows, searchUserBelumBeli]);

  const userBelumPembelianColumns = [
    {
      field: "user",
      headerName: "Nama User",
      minWidth: 145,
      flex: 1,
    },
    {
      field: "storeName",
      headerName: "Nama Toko",
      minWidth: 145,
      flex: 1,
    },
    {
      field: "area",
      headerName: "Area",
      minWidth: 115,
      flex: 0.75,
    },
    {
      field: "phoneNo",
      headerName: "Nomor HP",
      minWidth: 125,
      flex: 0.8,
    },
    {
      field: "tableNo",
      headerName: "Table",
      minWidth: 80,
      flex: 0.45,
    },
    {
      field: "address",
      headerName: "Alamat",
      minWidth: 220,
      flex: 1.2,
    },
  ];

  const UserBelumPembelianMobileCard = ({ row }) => (
    <Box
      sx={{
        ...glassPanel,
        p: 1.45,
        borderRadius: "18px",
      }}
    >
      <Typography
        sx={{
          ...poppins,
          color: "#fff",
          fontWeight: 900,
          fontSize: "0.92rem",
        }}
      >
        {row.user}
      </Typography>

      <Typography
        sx={{
          ...poppins,
          color: "rgba(219,234,254,0.62)",
          fontSize: "0.72rem",
          mt: 0.3,
        }}
      >
        {row.storeName || "-"}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          mt: 1.4,
        }}
      >
        <InfoLine
          icon={<LocationOnOutlinedIcon fontSize="small" />}
          label="Area"
          value={row.area}
        />

        <InfoLine
          icon={<LocalPhoneOutlinedIcon fontSize="small" />}
          label="Phone"
          value={row.phoneNo}
        />

        <InfoLine
          icon={<TableRestaurantOutlinedIcon fontSize="small" />}
          label="Table"
          value={row.tableNo}
        />

        <InfoLine
          icon={<StorefrontOutlinedIcon fontSize="small" />}
          label="Toko"
          value={row.storeName}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={pageWrapper}>
      <Box sx={{ mb: 1.7 }}>
        <Typography
          sx={{
            ...poppins,
            fontWeight: 900,
            fontSize: { xs: "1.35rem", md: "1.65rem" },
            color: "#fff",
            lineHeight: 1.15,
            textShadow: "0 0 22px rgba(96,165,250,0.45)",
          }}
        >
          Data Penjualan
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.72)",
            fontSize: "0.78rem",
            mt: 0.4,
          }}
        >
          Kelola data pembelian, kupon, kontrak, dan konfirmasi user
        </Typography>
      </Box>

      <Box sx={{ ...topPanel, mb: 1.6 }}>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
            <Box sx={iconBox}>
              <StorefrontOutlinedIcon />
            </Box>

            <Box>
              <Typography
                sx={{
                  ...poppins,
                  color: "#fff",
                  fontSize: "0.9rem",
                  fontWeight: 900,
                }}
              >
                Ringkasan Sales
              </Typography>

              <Typography
                sx={{
                  ...poppins,
                  color: "rgba(219,234,254,0.62)",
                  fontSize: "0.68rem",
                }}
              >
                Total data, konfirmasi, dan kupon
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, 1fr)",
              },
              gap: 1,
              mt: 1.6,
            }}
          >
            {[
              ["Data", totalSales],
              ["Konfirm", totalConfirmed],
              ["Kupon", totalCoupons],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  borderRadius: "14px",
                  p: 1.15,
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(147,197,253,0.16)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    ...poppins,
                    color: chartColors[index],
                    fontWeight: 900,
                    fontSize: { xs: "1.15rem", md: "1.35rem" },
                    lineHeight: 1,
                  }}
                >
                  {value}
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.58)",
                    fontSize: "0.64rem",
                    mt: 0.55,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },
          gap: 1.6,
          mb: 1.6,
          alignItems: "stretch",
        }}
      >
        <Box sx={topPanel}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 1.3 }}
            >
              <Box sx={iconBox}>
                <Inventory2OutlinedIcon />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    color: "#fff",
                    fontSize: "0.9rem",
                    fontWeight: 900,
                  }}
                >
                  Top Pembelian Paket
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.68rem",
                  }}
                >
                  Berdasarkan total quantity per paket
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 1,
              }}
            >
              {topPackages.length ? (
                topPackages.map((pkg) => renderTopPackageCard(pkg))
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.58)",
                    fontSize: "0.76rem",
                  }}
                >
                  Belum ada data paket.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={topPanel}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 1.3 }}
            >
              <Box sx={iconBox}>
                <EmojiEventsOutlinedIcon />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    color: "#fff",
                    fontSize: "0.9rem",
                    fontWeight: 900,
                  }}
                >
                  Top Pembelian Produk
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.68rem",
                  }}
                >
                  Top 5 user dengan quantity terbanyak
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gap: 1 }}>
              {topUsersByPurchase.length ? (
                topUsersByPurchase.map((item, index) =>
                  renderTopUserCard(item, index),
                )
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.58)",
                    fontSize: "0.76rem",
                  }}
                >
                  Belum ada data pembelian.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={tablePanel}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 1.1,
          }}
        >
          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.9rem",
              }}
            >
              Tabel Penjualan
            </Typography>

            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.58)",
                fontSize: "0.68rem",
                mt: 0.2,
              }}
            >
              Menampilkan {filteredRows.length} dari {rows.length} data
            </Typography>
          </Box>

          <Button
            onClick={exportToExcel}
            startIcon={<FileDownloadIcon />}
            sx={{
              ...poppins,
              color: "#fff",
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.74rem",
              border: "1px solid rgba(147,197,253,0.25)",
              background: "rgba(96,165,250,0.14)",
              borderRadius: "12px",
              px: 1.6,
              py: 0.75,
              "&:hover": {
                background: "rgba(96,165,250,0.22)",
              },
            }}
          >
            Export Excel
          </Button>
        </Box>

        <Box
          sx={{
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ color: "#93c5fd", display: "flex" }}>
            <SearchOutlinedIcon fontSize="small" />
          </Box>

          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cari nama user, toko, area, nomor HP, table, status, paket, atau quantity..."
            sx={searchInputSx}
          />
        </Box>

        {isMobile ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {filteredRows.map((row) => (
              <SalesMobileCard
                key={row.id}
                row={row}
                onCoupon={handleOpenCoupon}
                onConfirm={confirmSales}
                onDelete={deleteSales}
                onViewPdf={handleOpenViewDialog}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              borderRadius: "16px",
            }}
          >
            <DataGrid
              rows={filteredRows}
              columns={columns}
              sx={gridSx}
              autoHeight
              rowHeight={46}
              columnHeaderHeight={42}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Box sx={tablePanel}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 1.1,
          }}
        >
          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.9rem",
              }}
            >
              User belum melakukan pembelian
            </Typography>

            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.58)",
                fontSize: "0.68rem",
                mt: 0.2,
              }}
            >
              Menampilkan {filteredUsersBelumPembelian.length} dari{" "}
              {usersBelumPembelianRows.length} data
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ color: "#93c5fd", display: "flex" }}>
            <SearchOutlinedIcon fontSize="small" />
          </Box>

          <TextField
            fullWidth
            size="small"
            value={searchUserBelumBeli}
            onChange={(event) => setSearchUserBelumBeli(event.target.value)}
            placeholder="Cari nama user, toko, area, nomor HP, table"
            sx={searchInputSx}
          />
        </Box>

        {isMobile ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {filteredUsersBelumPembelian.map((row) => (
              <UserBelumPembelianMobileCard key={row.id} row={row} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              borderRadius: "16px",
            }}
          >
            <DataGrid
              rows={filteredUsersBelumPembelian}
              columns={userBelumPembelianColumns}
              sx={gridSx}
              autoHeight
              rowHeight={46}
              columnHeaderHeight={42}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      <DetailDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        selectedData={selectedTransaction}
      />

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />

      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: "linear-gradient(180deg, #091b3d 0%, #060f24 100%)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: isMobile ? 0 : "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...poppins,
            fontWeight: 900,
            color: "#fff",
            fontSize: "1rem",
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          Lihat Kontrak PDF
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: "rgba(255,255,255,0.08)",
            height: isMobile ? "calc(100vh - 135px)" : "80vh",
            p: { xs: 1, md: 1.5 },
          }}
        >
          {loading ? (
            <Typography
              sx={{
                ...poppins,
                color: "rgba(186,230,253,0.6)",
                textAlign: "center",
                mt: 4,
              }}
            >
              Mengambil data...
            </Typography>
          ) : contractData ? (
            <PDFViewer
              width="100%"
              height="100%"
              style={{
                border: "none",
                borderRadius: 8,
              }}
            >
              <ContractPDF
                contract={contractData.package}
                summary={contractData.summary}
                coupon={contractData.coupon}
                couponRange={contractData.couponRange}
                couponText={contractData.couponText}
                info={contractData.info}
                no={contractData.transactionNo}
              />
            </PDFViewer>
          ) : (
            <Typography
              sx={{
                ...poppins,
                color: "rgba(186,230,253,0.6)",
                textAlign: "center",
                mt: 4,
              }}
            >
              Tidak ada data untuk ditampilkan.
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.4,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={handleSendToWhatsApp}
            variant="contained"
            startIcon={<WhatsAppIcon />}
            fullWidth={isMobile}
            sx={{
              ...poppins,
              background: "linear-gradient(135deg, #16a34a, #15803d)",
              borderRadius: "11px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.78rem",
              py: 0.85,
              "&:hover": {
                background: "linear-gradient(135deg, #15803d, #166534)",
              },
            }}
          >
            Kirim ke WhatsApp
          </Button>

          <Button
            onClick={() => setOpenViewDialog(false)}
            fullWidth={isMobile}
            sx={{
              ...poppins,
              color: "rgba(186,230,253,0.8)",
              textTransform: "none",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "11px",
              px: 1.8,
              py: 0.85,
              fontWeight: 800,
              fontSize: "0.78rem",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.05)",
              },
            }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
